import { GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS } from 'client-portal/utils/clinicianSearch/facets/manifest';
import {
  canonicalCptCodeRecordForOffice,
  clearedBookingPreselectionProperties,
  narrowQueryParamsForClinician,
  officeIdsForPreselection,
  preselectionIdsForQueryParam,
  queryParamsWithClearedKeys,
} from 'client-portal/utils/clinicianSearch/preselection';
// eslint-disable-next-line no-restricted-imports -- downstream consumers (request.cptCodes, service route) rely on EmberArray methods (toArray/isAny/findBy/get('length')); refactoring them caused regressions.
import { A as emberA } from '@ember/array';
import { isoDateFormat } from 'client-portal/utils/date-time';
import { parseCommaSeparatedInts } from 'client-portal/utils/clinicianSearch/comma-query-param';
import {
  preselectedCptCodeFromServicesModel,
  preselectedRecordFromModel,
} from 'client-portal/utils/clinicianSearch/preselection/select-from-model';
import Service, { service } from '@ember/service';
import moment from 'moment-timezone';

const PRESELECTION_TRANSITIONS = new Set([
  'clinician-search>service',
  'clinician-search>location',
  'clinician-search>date',
  'service>location',
  'location>date',
]);

const STEP_SKIPPERS = {
  service: 'skipServiceStep',
  location: 'skipLocationStep',
};

/*
 * sessionReservation + sessionParams form the working set during a single model() pass.
 * Set by start() (click) or beginStep() (route hooks and model dispatchers).
 * Mutated by dropParams() when preselection finds no candidates.
 * Cleared by resetState() on booking exit.
 */

export default class ClinicianSearchPreselectionService extends Service {
  @service clinicianSearch;
  @service request;
  @service router;
  @service session;
  @service store;

  baseRouteController = null;

  // ---------- feature flag ----------

  get isEnabled() {
    return this.clinicianSearch.isEnabled;
  }

  // ---------- session state ----------

  start({ reservation, clinicianId, queryParams } = {}) {
    let narrowed = queryParams;
    if (this.isActiveParams(queryParams) && clinicianId) {
      narrowed = narrowQueryParamsForClinician({
        catalog: this.clinicianSearch.sendAll,
        clinicianId,
        queryParams,
      });
    }
    this.sessionReservation = reservation ?? null;
    this.sessionParams = { ...narrowed };
    return narrowed;
  }

  resetState() {
    this.baseRouteController?.setProperties(clearedBookingPreselectionProperties());

    this.baseRouteController = null;
    this.sessionReservation = null;
    this.sessionParams = null;
  }

  dropParams(keys) {
    this.sessionParams = queryParamsWithClearedKeys(this.sessionParams ?? {}, keys);
    return this.sessionParams;
  }

  beginStep(transition, { reservation } = {}) {
    if (!this.isEnabled) return;

    if (!this.isActiveTransition(transition)) {
      this.resetState();
      return;
    }

    this.sessionParams = this.paramsFromUrl(transition);
    if (reservation !== undefined) {
      this.sessionReservation = reservation ?? null;
    }
  }

  // ---------- url params ----------

  paramsFromUrl(transition, { controller } = {}) {
    let queryParams = { ...(transition?.to?.queryParams ?? {}) };
    let requestController = controller ?? this.baseRouteController;

    if (requestController) {
      for (let key of GROUP_SEARCH_PRESELECTION_QUERY_PARAM_KEYS) {
        queryParams[key] ??= requestController[key];
      }
    }

    return queryParams;
  }

  activeParams(transition) {
    return this.sessionParams ?? this.paramsFromUrl(transition);
  }

  // ---------- predicates ----------

  isActiveParams(params = {}) {
    if (!this.isEnabled) {
      return false;
    }

    return Boolean(params?.preselected);
  }

  isActiveTransition(transition = {}) {
    if (!this.isEnabled) {
      return false;
    }

    let queryParams = this.paramsFromUrl(transition);
    if (!queryParams?.preselected) {
      return false;
    }

    let fromLeaf = transition.from?.name?.split('.').pop();
    let toLeaf = transition.to?.name?.split('.').pop();
    if (!fromLeaf || fromLeaf === toLeaf) {
      return true;
    }

    return PRESELECTION_TRANSITIONS.has(`${fromLeaf}>${toLeaf}`);
  }

  singlePreselectedCptCodeId(transitionOrParams = {}) {
    let queryParams = transitionOrParams?.to?.queryParams
      ? this.paramsFromUrl(transitionOrParams)
      : transitionOrParams;
    if (!this.isActiveParams(queryParams)) {
      return null;
    }

    let ids = preselectionIdsForQueryParam(queryParams, 'preselectedCptCodeIds');
    return ids.length === 1 ? ids[0] : null;
  }

  // ---------- filter helpers (sibling routes) ----------

  get preselectedWeekdays() {
    return parseCommaSeparatedInts(this.sessionParams?.preselectedDays);
  }

  get preselectedTimeFilters() {
    let selectedBuckets = parseCommaSeparatedInts(this.sessionParams?.preselectedTimes);
    if (!selectedBuckets.length) {
      return null;
    }

    return {
      morning: selectedBuckets.some(bucket => bucket === 0 || bucket === 1),
      afternoon: selectedBuckets.includes(2),
      evening: selectedBuckets.includes(3),
    };
  }

  get shouldScanForPreferredDay() {
    return this.isEnabled && Boolean(this.sessionParams && this.preselectedWeekdays.length);
  }

  firstPreselectedWeekdayMatch(availableDateValues = []) {
    let weekdays = this.preselectedWeekdays;
    if (!weekdays.length) {
      return null;
    }

    return (
      [...availableDateValues]
        .sort()
        .find(date => weekdays.includes(moment(date, isoDateFormat).day())) ?? null
    );
  }

  preferredDay(availableDateValues, defaultActiveDate) {
    if (!this.shouldScanForPreferredDay) {
      return defaultActiveDate;
    }

    return (
      this.firstPreselectedWeekdayMatch(availableDateValues) ??
      defaultActiveDate ??
      availableDateValues[0]
    );
  }

  preferredMonthHop({ currentDate, currentMonthDateValues = [], nextMonthDateValues = [] } = {}) {
    if (!this.shouldScanForPreferredDay) {
      return null;
    }
    if (this.firstPreselectedWeekdayMatch(currentMonthDateValues)) {
      return null;
    }
    if (!this.firstPreselectedWeekdayMatch(nextMonthDateValues)) {
      return null;
    }

    return moment(currentDate).add(1, 'month').startOf('month').format(isoDateFormat);
  }

  applyTimeFiltersToController(controller) {
    if (!this.isEnabled) return;

    let preselected = this.preselectedTimeFilters;
    if (!this.sessionParams || !preselected) {
      return;
    }

    for (let filter of Object.keys(controller.filters)) {
      controller.set(`filters.${filter}.checked`, preselected[filter] ?? !controller.selectedDate);
    }
  }

  // ---------- booking flow ----------

  async findServices(transition) {
    if (this.sessionParams?.preselectedCptCodeIds) {
      transition.trigger(false, 'preselectionLoading', this.skeletonHintFor(this.sessionParams));
    }

    let models = this.serviceCandidates();
    if (!models.length) {
      return null;
    }

    let restricted = this.restrictByPreselection(models);
    return restricted ? emberA(restricted) : null;
  }

  async findOffices() {
    let params = this.sessionParams ?? {};
    if (params.searchByOffice) {
      return this.officeForSearchByOffice(params.officeId);
    }
    return this.preselectedOffices(params);
  }

  skipServiceStep(model, transition) {
    if (!this.isEnabled) return false;

    let params = this.activeParams(transition);
    let exactKey = params.cptCodeId ? 'cptCodeId' : 'preselectedCptCodeIds';
    return this.skipStep(model, transition, {
      exactKey,
      candidateKey: 'preselectedCptCodeIds',
      prop: 'cptCode',
    });
  }

  skipLocationStep(model, transition) {
    if (!this.isEnabled) return false;

    return this.skipStep(model, transition, {
      exactKey: 'officeId',
      candidateKey: 'preselectedOfficeIds',
      prop: 'office',
    });
  }

  // ---------- route dispatchers (no-op when inactive) ----------

  async modelForServiceStep(reservation, transition) {
    if (!this.isEnabled) return null;

    return this.ensureCatalogIfEnabled(
      transition,
      () => this.findServices(transition),
      reservation
    );
  }

  async modelForLocationStep(reservation, transition) {
    if (!this.isEnabled) return null;

    return this.ensureCatalogIfEnabled(
      transition,
      async () => {
        let offices = await this.findOffices();
        return offices ? { geo: false, offices } : null;
      },
      reservation
    );
  }

  async ensureCatalogIfEnabled(transition, getModel, reservation) {
    if (!this.isActiveTransition(transition)) {
      return null;
    }

    this.beginStep(transition, { reservation });
    await this.clinicianSearch.sendAll.ensureLoaded();
    return getModel();
  }

  skipStepIfActive(step, model, transition) {
    if (!this.isEnabled) return false;

    if (!this.isActiveTransition(transition)) {
      return false;
    }

    let method = STEP_SKIPPERS[step];
    return method ? Boolean(this[method](model, transition)) : false;
  }

  next({ office } = {}) {
    if (!this.isEnabled) return;

    if (!office || !this.sessionReservation || !this.isActiveParams(this.sessionParams)) {
      return;
    }

    let canonical = canonicalCptCodeRecordForOffice({
      catalog: this.clinicianSearch.sendAll,
      store: this.store,
      clinicianId: this.sessionReservation.get('clinician.id'),
      officeId: office.id,
      preselectedCptCodeIds: this.sessionParams.preselectedCptCodeIds,
    });
    if (canonical) {
      this.sessionReservation.set('cptCode', canonical);
    }
  }

  // ---------- private ----------

  skeletonHintFor(queryParams = {}) {
    if (!this.isActiveParams(queryParams)) {
      return null;
    }

    let cptIds = preselectionIdsForQueryParam(queryParams, 'preselectedCptCodeIds');
    let officeIds = preselectionIdsForQueryParam(queryParams, 'preselectedOfficeIds');

    if (cptIds.length !== 1) {
      return 'services';
    }
    return officeIds.length > 1 ? 'locations' : 'availability';
  }

  officeForSearchByOffice(officeId) {
    let exact = String(officeId ?? '').trim();
    let office = exact ? this.store.peekRecord('office', exact) : null;
    return office ? [office] : null;
  }

  preselectedOffices(params) {
    let { preselectedOfficeIds } = params;

    if (!this.isActiveParams(params)) {
      return null;
    }

    let practiceOffices = this.store.peekAll('office').toArray();
    let clinicianId = this.sessionReservation.get('clinician.id');
    let allow = officeIdsForPreselection({
      catalog: this.clinicianSearch.sendAll,
      clinicianId,
      queryParams: params,
      reservationCptCodeId: this.sessionReservation.get('cptCode.id'),
    });
    if (!allow.size && !preselectedOfficeIds) {
      return null;
    }

    let narrowed = practiceOffices.filter(office => allow.has(office.id));
    return narrowed.length ? narrowed : null;
  }

  serviceCandidates() {
    let queryParams = this.sessionParams ?? {};
    let clinicianId = this.sessionReservation.get('clinician.id');
    let officeIds = preselectionIdsForQueryParam(queryParams, 'preselectedOfficeIds');

    let rateIds = this.clinicianSearch.sendAll.cptCodeRateIdsForClinician(clinicianId, {
      officeIds,
    });
    let codes = rateIds.map(id => this.store.peekRecord('cpt-code', id)).filter(Boolean);

    if (this.session.isMonarchEmbedded) {
      codes = codes.filter(code => code.callToBook !== true);
    }

    return codes;
  }

  restrictByPreselection(models) {
    let restrictCptIds = preselectionIdsForQueryParam(this.sessionParams, 'preselectedCptCodeIds');
    if (!restrictCptIds.length) {
      return models;
    }

    let allow = new Set(restrictCptIds.map(String));
    let restricted = models.filter(model => allow.has(String(model.id)));
    return restricted.length ? restricted : null;
  }

  skipStep(model, transition, { exactKey, candidateKey, prop }) {
    let params = this.activeParams(transition);
    if (!this.isActiveParams(params)) {
      transition.trigger(false, 'stopPreselectionLoading', transition);
      return;
    }

    let picker =
      prop === 'cptCode' ? preselectedCptCodeFromServicesModel : preselectedRecordFromModel;
    let found = picker(model, params, exactKey, candidateKey);

    if (found) {
      this.sessionReservation?.set(prop, found);
      this.clinicianSearch.preselectionNavigation.beginSession(params, transition);
      transition.trigger(false, 'nextStep', found, { queryParams: params });
      return true;
    }

    let invalid = [exactKey, candidateKey].filter(key => params?.[key]);
    if (invalid.length) {
      this.dropParams(invalid);
    }

    transition.trigger(false, 'stopPreselectionLoading', transition);
  }
}
